import { useEffect, useState } from 'react';
import { getPermissionResourceKeyFromPath } from '@/lib/permissions';
import { useAuth } from '@/contexts/auth-context';
import { toast } from '@/lib/hot-toast';

/**
 * Hook for managing form state
 * @template T The type of the selected object (must have pt_tao_yn property)
 * @template K The type of the row ID (string | number)
 * @returns Form state and functions to manage it
 */
type FetchDetailFn = (uuid: string) => Promise<BanHangRecord>;
const OPEN_RECORD_PARAM = 'erp-view-record';

export const useForm = (fetchDetail?: FetchDetailFn) => {
  const { permissions, isAdmin } = useAuth();
  const [showForm, setShowForm] = useState<boolean>(false);
  const [showSearch, setShowSearch] = useState<boolean>(false);
  const [showDelete, setShowDelete] = useState<boolean>(false);
  const [showCantEdit, setShowCantEdit] = useState<boolean>(false);
  const [formMode, setFormMode] = useState<'add' | 'edit' | 'view'>('add');
  const [selectedRowIndex, setSelectedRowIndex] = useState<BanHangRecord | null>(null);
  const [selectedObj, setSelectedObj] = useState<BanHangRecord | null>(null);

  const [isCopyMode, setIsCopyMode] = useState<boolean>(false);
  const [showBulkDelete, setShowBulkDelete] = useState<boolean>(false);

  const getPermission = (action: 'create' | 'update' | 'delete') => {
    if (isAdmin) return true;
    const resourceKey = getPermissionResourceKeyFromPath(window.location.pathname);
    return resourceKey ? permissions[resourceKey]?.[action] === true : false;
  };

  /**
   * Handle closing the form
   */
  const handleCloseForm = () => {
    setShowForm(false);
  };

  /**
   * Handle closing the delete confirmation dialog
   */
  const handleCloseDelete = () => {
    setShowDelete(false);
  };

  /**
   * Handle clicking the add button
   */
  const handleAddClick = () => {
    if (!getPermission('create')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    setFormMode('add');
    setIsCopyMode(false);
    setShowForm(true);
  };

  /**
   * Handle clicking the edit button
   */
  const handleEditClick = () => {
    if (!getPermission('update')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    if (!selectedObj?.pt_tao_yn && (selectedObj?.giay_bao_co_uuids?.[0] || selectedObj?.phieu_thu_uuids?.[0])) {
      setShowCantEdit(true);
      return;
    } else {
      setFormMode('edit');
      setShowForm(true);
    }
  };

  /**
   * Handle clicking the view button
   */
  const handleViewClick = async (row?: BanHangRecord) => {
    const target = row ?? selectedObj;

    if (!target?.uuid) return;

    if (row) {
      setSelectedObj(row);
      setSelectedRowIndex(row.uuid);
    }

    if (fetchDetail && target?.uuid !== selectedObj?.uuid) {
      await fetchDetail(target?.uuid);
    }

    setFormMode('view');
    setShowForm(true);
  };

  /**
   * Handle clicking the delete button
   */
  const handleDeleteClick = () => {
    if (!getPermission('delete')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    setShowDelete(true);
  };

  /**
   * Handle clicking the copy button
   */
  const handleCopyClick = () => {
    if (!getPermission('create')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    setFormMode('add');
    setIsCopyMode(true);
    setShowForm(true);
  };

  /**
   * Handle clicking a row in the data table
   * @param params The row parameters containing row data and id
   */
  const handleRowClick = async (params: { id: string; row: BanHangRecord }) => {
    setSelectedRowIndex(params.id);
    setSelectedObj(params.row);
    if (fetchDetail && params.row.uuid !== selectedObj?.uuid) {
      await fetchDetail(params.row.uuid);
    }
  };

  const handleSearch = () => {
    setShowSearch(true);
  };

  const handleCloseSearch = () => {
    setShowSearch(false);
  };

  const clearSelection = () => {
    setSelectedRowIndex(null);
    setSelectedObj(null);
  };

  /**
   * Handle closing the bulk delete dialog
   */
  const handleCloseBulkDelete = () => {
    setShowBulkDelete(false);
  };
  const handleImportExcel = (openImportDialog: () => void) => {
    if (!getPermission('create')) {
      toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
      return;
    }
    openImportDialog();
  };

  useEffect(() => {
    if (!fetchDetail) return;

    const recordUuid = new URLSearchParams(window.location.search).get(OPEN_RECORD_PARAM);
    if (!recordUuid) return;

    let active = true;
    void fetchDetail(recordUuid).then(record => {
      if (!active || !record) return;

      setSelectedObj(record);
      setSelectedRowIndex(record.uuid);
      setFormMode('view');
      setShowForm(true);
    });

    return () => {
      active = false;
    };
  }, [fetchDetail]);

  useEffect(() => {
    const handleGlobalShortcut = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();
        if (!getPermission('delete')) {
          toast.error('Bạn chưa được phân quyền để thực hiện tính năng này');
          return;
        }
        setShowBulkDelete(true);
      }
    };

    window.addEventListener('keydown', handleGlobalShortcut);
    return () => window.removeEventListener('keydown', handleGlobalShortcut);
  }, [permissions, isAdmin]);
  return {
    showForm,
    showSearch,
    showDelete,
    formMode,
    selectedRowIndex,
    isCopyMode,
    showCantEdit,
    setShowCantEdit,
    selectedObj,
    showBulkDelete,
    handleCloseForm,
    handleCloseDelete,
    handleAddClick,
    handleEditClick,
    handleViewClick,
    handleDeleteClick,
    handleCopyClick,
    handleRowClick,
    clearSelection,
    handleSearch,
    handleCloseSearch,
    handleCloseBulkDelete,
    handleImportExcel
  };
};
